import React from 'react';
import { Grid } from '@mui/material';
import moment, { Moment } from 'moment';
import { Fields } from '@pv/common/components';

interface Venue {
  eventStartOffset?: number | string;
  eventEndOffset?: number | string;
}

interface EventTimeEditProps {
  startDate: string | Moment | null;
  startOffset?: number | null;
  endDate?: string | Moment | null;
  endOffset?: number | null;
  setStartDate: (d: Moment) => void;
  setStartOffset: (offset: number) => void;
  setEndDate?: (d: Moment) => void;
  setEndOffset: (offset: number) => void;
  multiDayEnabled?: boolean;
  venue: Venue | null;
}

const TIME_INTERVAL = 15;
const SIX_AM = 6 * 60;
const FOUR_AM = 28 * 60;

export function computeEndTimeRangeStart({
  venue,
  startOffset,
  startDate,
  endDate,
  multiDayEnabled,
}: {
  venue: Venue;
  startOffset?: number | null;
  startDate: string | Moment | null;
  endDate?: string | Moment | null;
  multiDayEnabled: boolean;
}): number {
  const startTimeStart = parseInt(String(venue.eventStartOffset || SIX_AM));
  const endTimeStart = startTimeStart + TIME_INTERVAL;
  const isMultiDay =
    multiDayEnabled &&
    endDate &&
    startDate &&
    !moment(startDate).isSame(endDate, 'day');
  if (isMultiDay) return endTimeStart;
  return parseInt(String(startOffset)) || endTimeStart;
}

export const EventTimeEdit = ({
  startDate,
  startOffset,
  endDate = startDate,
  endOffset,
  setStartDate,
  setStartOffset,
  setEndDate = () => {},
  setEndOffset,
  multiDayEnabled = false,
  venue,
}: EventTimeEditProps) => {
  if (!venue) {
    return null;
  }

  const startTimeStart = parseInt(String(venue.eventStartOffset || SIX_AM));
  const startTimeEnd =
    parseInt(String(venue.eventEndOffset || FOUR_AM)) - TIME_INTERVAL;

  const endTimeEnd = startTimeEnd + TIME_INTERVAL;

  const startDateLabel = multiDayEnabled ? 'Start Date' : 'Date';
  const dateXs = 12;
  const timeXs = 12;
  const dateSm = multiDayEnabled ? 6 : 4;
  const timeSm = multiDayEnabled ? 6 : 4;

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={dateXs} sm={dateSm}>
            {/* @ts-expect-error Fields.PvDateField types mark optional props as required */}
            <Fields.PvDateField
              fullWidth
              name="startDate"
              label={startDateLabel}
              onChange={(d: Moment) => setStartDate(d)}
              value={startDate && moment(startDate)}
              testId="create-event-date-text-field"
            />
          </Grid>
          <Grid item xs={timeXs} sm={timeSm}>
            {/* @ts-expect-error Fields.PvTimeField types mark optional props as required */}
            <Fields.PvTimeField
              label="Start"
              name="startTime"
              start={startTimeStart}
              end={startTimeEnd}
              value={startOffset || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setStartOffset(parseInt(e.target.value))
              }
              selectTestId="create-event-start-time-select"
            />
          </Grid>
          {multiDayEnabled && (
            <Grid item xs={dateXs} sm={dateSm}>
              {/* @ts-expect-error Fields.PvDateField types mark optional props as required */}
              <Fields.PvDateField
                fullWidth
                name="endDate"
                label="End Date"
                onChange={(d: Moment) => setEndDate(d)}
                value={endDate && moment(endDate)}
              />
            </Grid>
          )}
          <Grid item xs={timeXs} sm={timeSm}>
            {/* @ts-expect-error Fields.PvTimeField types mark optional props as required */}
            <Fields.PvTimeField
              label="End"
              name="endTime"
              start={computeEndTimeRangeStart({
                venue,
                startOffset,
                startDate,
                endDate,
                multiDayEnabled,
              })}
              end={endTimeEnd}
              value={endOffset || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setEndOffset(parseInt(e.target.value))
              }
              selectTestId="create-event-end-time-select"
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default EventTimeEdit;
